import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { FileText, Play, Download, Lock, CheckCircle2, AlertCircle, Menu, X, MapPin, Phone, User, Mail, MessageSquare } from 'lucide-react';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

// Assets from the scrape catalog
const LOGO_URL = "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/logomediccare.svg";
const MEDIMI_IMAGE = "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/medimi.jpg";
const HELHEDSLOESNING_IMAGE = "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/helhedsloesning.jpg";
const PDF_URL = "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/broschyr-medicpen-dk-web.pdf";

// Flow images
const FLOW_IMAGES = {
  ordination: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/ordination.png",
  omsorgssystem: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/omsorgssystem.png",
  flextech: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/flextech.png",
  medicin: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/medicin.png",
  levering: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/levering.png",
  dosering: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/dosering.png"
};

// Partner images
const PARTNER_IMAGES = {
  medicpen: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/medicpen.svg",
  careconsult: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/cc-logo-grey.svg",
  apotek: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/apotek.svg",
  atea: "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/atea-logo-generated.svg"
};

// Map image
const MAP_IMAGE = "https://assets.auxodev.dk/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/vt_1.jpg";

// Menu configuration
const MENU_ITEMS = [
  { label: "Hjem", path: "" },
  { label: "MedimiSmart®", path: "medimismart.php" },
  { label: "Folder", path: "folder.php" },
  { label: "Video", path: "video.php" },
  { label: "Om MedicCare", path: "om-mediccare.php" },
  { label: "TIM", path: "TIM/index.php" },
  { label: "Intern", path: "intern/index.php" },
  { label: "Apotek", path: "apotek/index.php" },
  { label: "Sverige", path: "Sverige/index.php" },
  { label: "Kontakt", path: "kontakt.php" },
  { label: "BorgerScreening", path: "borgerscreening.php" }
];

// Helper to clean paths for router matching
const getCleanPath = (path) => {
  if (path === "") return "";
  return path;
};

export default function GammelSideApp() {
  useDocumentMetadata({
    title: 'Arkiv | MedicCare',
    description: 'Arkivside',
    noindex: true
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const currentSubPath = location.pathname.replace("/gammelside", "").replace(/^\//, "");

  return (
    <div className="min-h-screen bg-[#f4f5f2] text-[#333333] font-sans antialiased">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-[#dcded9] p-4 flex justify-between items-center sticky top-0 z-50 shadow-sm">
        <Link to="/gammelside" className="flex items-center">
          <img src={LOGO_URL} alt="MedicCare Logo" className="h-10 w-auto" />
        </Link>
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 text-[#2c3223] hover:bg-[#e9eae7] rounded"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Main Layout Container */}
      <div className="max-w-7xl mx-auto px-4 py-6 lg:py-12 flex flex-col lg:flex-row gap-8">
        
        {/* Sidebar (Left Column) */}
        <aside className={`w-full lg:w-80 shrink-0 flex flex-col gap-6 ${mobileMenuOpen ? 'block' : 'hidden lg:flex'}`}>
          
          {/* Logo & Quote Box */}
          <div className="bg-white border border-[#dcded9] p-6 rounded shadow-sm flex flex-col items-center text-center">
            <Link to="/gammelside" className="mb-6 block" onClick={() => setMobileMenuOpen(false)}>
              <img src={LOGO_URL} alt="MedicCare Logo" className="h-16 w-auto" />
            </Link>
            
            <div className="border-t border-b border-[#e9eae7] py-4 my-2 w-full">
              <p className="italic text-[#555a4f] font-medium text-sm leading-relaxed">
                “Enkel, sikker og<br />
                minimere risikoen<br />
                for fejlmedicinering.”
              </p>
            </div>

            <div className="mt-4">
              <img 
                src={MEDIMI_IMAGE} 
                alt="Medimi Device" 
                className="max-w-[180px] h-auto rounded border border-[#e9eae7] shadow-inner"
              />
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="bg-white border border-[#dcded9] rounded shadow-sm overflow-hidden">
            <div className="bg-[#2c3223] text-white px-4 py-3 font-bold text-sm uppercase tracking-wider">
              Menu
            </div>
            <ul className="divide-y divide-[#e9eae7]">
              {MENU_ITEMS.map((item) => {
                const isHome = item.path === "";
                const isActive = isHome 
                  ? currentSubPath === "" || currentSubPath === "index.php"
                  : currentSubPath === item.path;

                return (
                  <li key={item.label}>
                    <Link
                      to={`/gammelside/${item.path}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block px-4 py-3 text-sm font-medium transition-colors ${
                        isActive 
                          ? 'bg-[#e9eae7] text-[#2c3223] border-l-4 border-[#2c3223] pl-3' 
                          : 'text-[#555a4f] hover:bg-[#fcfdfa] hover:text-[#2c3223]'
                      }`}
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Back to New Site Button */}
          <Link 
            to="/" 
            className="bg-[#2c3223] text-white text-xs font-bold uppercase tracking-wider py-3 px-4 rounded text-center hover:bg-[#1e2318] transition-colors shadow-sm"
          >
            Tilbage til den nye side
          </Link>
        </aside>

        {/* Content Area (Right Column) */}
        <main className="flex-1 bg-white border border-[#dcded9] p-6 lg:p-10 rounded shadow-sm min-h-[600px]">
          <Routes>
            <Route path="" element={<HjemPage />} />
            <Route path="index.php" element={<HjemPage />} />
            <Route path="medimismart.php" element={<MedimiSmartPage />} />
            <Route path="folder.php" element={<FolderPage />} />
            <Route path="video.php" element={<VideoPage />} />
            <Route path="om-mediccare.php" element={<OmMedicCarePage />} />
            <Route path="TIM/index.php" element={<PasswordPage title="TIM" />} />
            <Route path="intern/index.php" element={<PasswordPage title="Intern" />} />
            <Route path="apotek/index.php" element={<PasswordPage title="Apotek" />} />
            <Route path="Sverige/index.php" element={<PasswordPage title="TIM" />} />
            <Route path="kontakt.php" element={<KontaktPage />} />
            <Route path="borgerscreening.php" element={<BorgerScreeningPage />} />
          </Routes>
        </main>

      </div>

      {/* Footer */}
      <footer className="bg-[#2c3223] text-[#c3c7bd] py-8 mt-12 border-t border-[#1e2318]">
        <div className="max-w-7xl mx-auto px-4 text-center text-xs space-y-2">
          <p className="font-semibold text-white">MedicCare © 2026</p>
          <p>Enkel, sikker og minimerer risikoen for fejlmedicinering</p>
          <div className="pt-4 flex justify-center gap-6 text-[11px]">
            <Link to="/" className="hover:text-white transition-colors">Gå til nyeste version af MedicCare.dk</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ==========================================
   PAGE COMPONENTS
   ========================================== */

// 1. Hjem Page
function HjemPage() {
  return (
    <div className="space-y-8">
      <div className="border-b border-[#e9eae7] pb-4">
        <h1 className="text-2xl lg:text-3xl font-bold text-[#2c3223] leading-tight">
          Totalløsning til Intelligent Medicinhåndtering (TIM) - både for borger og sundhedspersonale
        </h1>
      </div>

      <div className="prose max-w-none text-[#555a4f] leading-relaxed space-y-6">
        <p className="text-base">
          Medimi®Smart er mere end en intelligent pilledispenser. Når Medimi®Smart integreres med et omsorgssystem, får du en helhedsløsning fra lægens ordination til borgeren hånd.
        </p>
      </div>

      <div className="pt-6 border-t border-[#e9eae7]">
        <img 
          src={HELHEDSLOESNING_IMAGE} 
          alt="Helhedsløsning" 
          className="w-full h-auto rounded border border-[#dcded9] shadow-sm"
        />
      </div>
      
      <div className="text-right text-xs text-[#8c9185] italic pt-4">
        MedicCare
      </div>
    </div>
  );
}

// 2. MedimiSmart Page
function MedimiSmartPage() {
  return (
    <div className="space-y-10">
      <div className="border-b border-[#e9eae7] pb-4">
        <h1 className="text-2xl lg:text-3xl font-bold text-[#2c3223]">
          Medimi®Smart 3D modeller
        </h1>
      </div>

      {/* 3D Models Box */}
      <div className="bg-[#fcfdfa] border border-[#dcded9] p-6 rounded space-y-4">
        <p className="text-sm font-medium text-[#555a4f]">
          (Virker kun på nyere Apple enheder) -&gt;{' '}
          <a href="https://mediccare.dk/AR/MS8.usdz" className="text-[#2c3223] underline font-bold hover:text-[#1e2318]">MS8</a> |{' '}
          <a href="https://mediccare.dk/AR/MS12.usdz" className="text-[#2c3223] underline font-bold hover:text-[#1e2318]">MS12</a> |{' '}
          <a href="https://mediccare.dk/AR/MS16.usdz" className="text-[#2c3223] underline font-bold hover:text-[#1e2318]">MS16</a>
        </p>
        <p className="text-sm font-medium text-[#555a4f]">
          (AR til Android) -&gt;{' '}
          <a href="https://mediccare.dk/AR/dummy_12.glb" className="text-[#2c3223] underline font-bold hover:text-[#1e2318]">MS12</a>
        </p>
      </div>

      {/* Helhedsløsning Section */}
      <div className="space-y-6">
        <h2 className="text-xl lg:text-2xl font-bold text-[#2c3223]">
          En smart “helhedsløsning”
        </h2>
        <ul className="space-y-4 text-[#555a4f] text-sm leading-relaxed list-disc pl-5">
          <li>
            Medimi®Smart har flere forskellige påmindelsesfunktioner, som sikrer at borgeren ikke misser sin medicinering. Hvis borgeren ikke reagerer på lys og lydsignalerne, sendes en SMS til borgeren.
          </li>
          <li>
            Inden det er for sent sendes alarm til Falck, som kontakter borgeren.
          </li>
          <li>
            På hver kassette findes en QR kode. Denne kontrollerer Medimi®Smart, inden medicinen doseres. Derved dispenseres medicinen efter senest opdaterede ordinationer.
          </li>
          <li>
            Medimi®Smart påminder kun, såfremt en borger er ved at glemme, dermed slipper borgeren for unødige påmindelser.
          </li>
        </ul>
      </div>

      {/* YouTube Video Embed */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-[#2c3223]">Velfærdens Innovationsdag 2018: MedicCare - assisteret medicineringshjælp</h3>
        <div className="aspect-video w-full bg-black rounded overflow-hidden shadow-md">
          <iframe 
            className="w-full h-full"
            src="https://www.youtube.com/embed/DDM5mDRWm_k" 
            title="Velfærdens Innovationsdag 2018: MedicCare"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </div>

      {/* Process Flow Steps */}
      <div className="space-y-8 pt-6 border-t border-[#e9eae7]">
        <h2 className="text-xl lg:text-2xl font-bold text-[#2c3223] text-center mb-8">
          Arbejdsgangen i systemet
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Step 1 */}
          <div className="border border-[#dcded9] p-5 rounded bg-[#fcfdfa] flex flex-col items-center text-center">
            <img src={FLOW_IMAGES.ordination} alt="Ordination" className="h-24 object-contain mb-4" />
            <h3 className="font-bold text-[#2c3223] text-base mb-2">Ordination</h3>
            <p className="text-xs text-[#555a4f] leading-relaxed">
              Lægen ordinerer, medicinformation kommer automatisk over i omsorgssystemet via FMK, som igen sendes videre til Apoteket.
            </p>
          </div>

          {/* Step 2 */}
          <div className="border border-[#dcded9] p-5 rounded bg-[#fcfdfa] flex flex-col items-center text-center">
            <img src={FLOW_IMAGES.omsorgssystem} alt="Fælles Medicin Kort" className="h-24 object-contain mb-4" />
            <h3 className="font-bold text-[#2c3223] text-base mb-2">Fælles Medicin Kort &amp; Omsorgssystem</h3>
            <p className="text-xs text-[#555a4f] leading-relaxed">
              Medicin information fra læge og apotek kommer via FMK til kommunernes omsorgssystem.
            </p>
          </div>

          {/* Step 3 */}
          <div className="border border-[#dcded9] p-5 rounded bg-[#fcfdfa] flex flex-col items-center text-center">
            <img src={FLOW_IMAGES.flextech} alt="FlexSuite" className="h-24 object-contain mb-4" />
            <h3 className="font-bold text-[#2c3223] text-base mb-2">FlexSuite</h3>
            <p className="text-xs text-[#555a4f] leading-relaxed">
              Systemet der forbinder information mellem omsorgssystem, Apotek samt Medimi®Smart.
            </p>
          </div>

          {/* Step 4 */}
          <div className="border border-[#dcded9] p-5 rounded bg-[#fcfdfa] flex flex-col items-center text-center">
            <img src={FLOW_IMAGES.medicin} alt="Medicin" className="h-24 object-contain mb-4" />
            <h3 className="font-bold text-[#2c3223] text-base mb-2">Medicin</h3>
            <p className="text-xs text-[#555a4f] leading-relaxed">
              Medicin ompakkes til kassetter af Apoteket
            </p>
          </div>

          {/* Step 5 */}
          <div className="border border-[#dcded9] p-5 rounded bg-[#fcfdfa] flex flex-col items-center text-center col-span-1 md:col-span-2 max-w-md mx-auto w-full">
            <img src={FLOW_IMAGES.levering} alt="Leverance" className="h-24 object-contain mb-4" />
            <h3 className="font-bold text-[#2c3223] text-base mb-2">Leverance</h3>
            <p className="text-xs text-[#555a4f] leading-relaxed">
              Atea leverer kassetter direkte til borgeren og sætter disse ind i Medimi®Smart.
            </p>
          </div>

          {/* Step 6 */}
          <div className="border border-[#dcded9] p-5 rounded bg-[#fcfdfa] flex flex-col items-center text-center col-span-1 md:col-span-2 max-w-md mx-auto w-full">
            <img src={FLOW_IMAGES.dosering} alt="Dosering" className="h-24 object-contain mb-4" />
            <h3 className="font-bold text-[#2c3223] text-base mb-2">Dosering</h3>
            <p className="text-xs text-[#555a4f] leading-relaxed">
              Medimi®Smart doserer og påminder borgeren, på denne måde sker medicineringen under kontrollerede former og risikoen for fejldosering minskes markant.
            </p>
          </div>
        </div>
      </div>

      <div className="pt-8 border-t border-[#e9eae7]">
        <img 
          src={HELHEDSLOESNING_IMAGE} 
          alt="Helhedsløsning" 
          className="w-full h-auto rounded border border-[#dcded9]"
        />
      </div>

      <div className="text-right text-xs text-[#8c9185] italic pt-4">
        MedicCare
      </div>
    </div>
  );
}

// 3. Folder Page
function FolderPage() {
  return (
    <div className="space-y-8">
      <div className="border-b border-[#e9eae7] pb-4">
        <h1 className="text-2xl lg:text-3xl font-bold text-[#2c3223]">
          Læs eller download vores folder her
        </h1>
      </div>

      <div className="bg-[#fcfdfa] border border-[#dcded9] p-8 rounded text-center space-y-6 max-w-lg mx-auto">
        <div className="flex justify-center">
          <div className="bg-[#e9eae7] p-4 rounded-full text-[#2c3223]">
            <FileText size={48} />
          </div>
        </div>
        
        <div className="space-y-2">
          <h3 className="font-bold text-lg text-[#2c3223]">Broschyr - MedicPen</h3>
          <p className="text-sm text-[#555a4f]">Få et komplet overblik over Medimi®Smart systemet og dets fordele.</p>
        </div>

        <div className="pt-4">
          <a 
            href={PDF_URL} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#2c3223] text-white font-bold text-sm uppercase tracking-wider px-6 py-3 rounded hover:bg-[#1e2318] transition-colors shadow-sm"
          >
            <Download size={18} /> Download PDF
          </a>
        </div>
      </div>
    </div>
  );
}

// 4. Video Page
function VideoPage() {
  const videos = [
    { title: "Intelligent Medicinhåndtering (Ny mt)", filename: "Intelligent_Medicinh%C3%A5ndtering_mt.mp4" },
    { title: "Medicin til tiden (Ny mt)", filename: "Medicin_til_tiden%20mt.mp4" },
    { title: "Intelligent Medicinhåndtering", filename: "MedimiSmartSamlet.mp4" },
    { title: "Medicin til tiden", filename: "medicin-til-tiden.mp4" },
    { title: "Fremtidig medicin", filename: "fremtidig-medicin.mp4" },
    { title: "Behovsmedicin", filename: "behovsmedicin.mp4" },
    { title: "Instruktionsvideo", filename: "dummy.mov" }
  ];

  return (
    <div className="space-y-8">
      <div className="border-b border-[#e9eae7] pb-4">
        <h1 className="text-2xl lg:text-3xl font-bold text-[#2c3223]">
          Videoer
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {videos.map((video, idx) => (
          <div key={idx} className="border border-[#dcded9] p-4 rounded bg-[#fcfdfa] flex flex-col justify-between space-y-4">
            <h3 className="font-bold text-[#2c3223] text-sm border-b border-[#e9eae7] pb-2">
              {video.title}
            </h3>
            
            <div className="aspect-video bg-black rounded flex items-center justify-center relative overflow-hidden">
              <video 
                src={`https://mediccare.dk/video/${video.filename}`}
                controls
                className="w-full h-full object-cover"
                preload="none"
              />
            </div>

            <div className="pt-2 text-center">
              <a 
                href={`https://mediccare.dk/video/${video.filename}`}
                download
                className="inline-flex items-center gap-2 text-xs font-bold text-[#2c3223] hover:underline"
              >
                <Download size={14} /> Download video
              </a>
            </div>
          </div>
        ))}
      </div>

      <div className="text-right text-xs text-[#8c9185] italic pt-4">
        MedicCare
      </div>
    </div>
  );
}

// 5. Om MedicCare Page
function OmMedicCarePage() {
  return (
    <div className="space-y-10">
      <div className="border-b border-[#e9eae7] pb-4">
        <h1 className="text-2xl lg:text-3xl font-bold text-[#2c3223]">
          Konsortium &amp; Samarbejdspartnere
        </h1>
      </div>

      {/* Konsortium */}
      <div className="space-y-6">
        <h2 className="text-xl font-bold text-[#2c3223] border-b border-[#e9eae7] pb-2">
          Konsortium
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <a 
            href="http://medimiab.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="border border-[#dcded9] p-6 rounded bg-[#fcfdfa] flex flex-col items-center text-center hover:shadow-md transition-shadow"
          >
            <img src={PARTNER_IMAGES.medicpen} alt="Medimi AB" className="h-16 object-contain mb-4" />
            <h3 className="font-bold text-[#2c3223] text-base mb-1">Medimi AB</h3>
            <p className="text-xs text-[#555a4f]">Enkel og sikker medicinering i hjemmet.</p>
          </a>

          <a 
            href="https://www.careconsult.dk/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="border border-[#dcded9] p-6 rounded bg-[#fcfdfa] flex flex-col items-center text-center hover:shadow-md transition-shadow"
          >
            <img src={PARTNER_IMAGES.careconsult} alt="CareConsult A/S" className="h-16 object-contain mb-4" />
            <h3 className="font-bold text-[#2c3223] text-base mb-1">CareConsult A/S</h3>
            <p className="text-xs text-[#555a4f]">Social og sundhedsinformatik</p>
          </a>
        </div>
      </div>

      {/* Samarbejdspartnere */}
      <div className="space-y-6 pt-6">
        <h2 className="text-xl font-bold text-[#2c3223] border-b border-[#e9eae7] pb-2">
          Samarbejdspartnere
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <a 
            href="https://www.apoteket.dk/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="border border-[#dcded9] p-6 rounded bg-[#fcfdfa] flex flex-col items-center text-center hover:shadow-md transition-shadow"
          >
            <img src={PARTNER_IMAGES.apotek} alt="Apotek" className="h-16 object-contain mb-4" />
            <h3 className="font-bold text-[#2c3223] text-base mb-1">Apotek</h3>
            <p className="text-xs text-[#555a4f]">Ompakning af medicin til vores MedimiSmart kassetter.</p>
          </a>

          <a 
            href="https://www.atea.dk/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="border border-[#dcded9] p-6 rounded bg-[#fcfdfa] flex flex-col items-center text-center hover:shadow-md transition-shadow"
          >
            <img src={PARTNER_IMAGES.atea} alt="Atea" className="h-16 object-contain mb-4" />
            <h3 className="font-bold text-[#2c3223] text-base mb-1">Atea</h3>
            <p className="text-xs text-[#555a4f]">Vagtcentral, Udkørsel, service til borgere og kommuner</p>
          </a>
        </div>
      </div>

      <div className="text-right text-xs text-[#8c9185] italic pt-4">
        MedicCare
      </div>
    </div>
  );
}

// 6. Password Page (TIM, Intern, Apotek, Sverige)
function PasswordPage({ title }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(true); // Always show error since we don't have real backend auth for the old site's dummy page
  };

  return (
    <div className="space-y-8 max-w-md mx-auto pt-10">
      <div className="border-b border-[#e9eae7] pb-4 text-center">
        <h1 className="text-2xl font-bold text-[#2c3223]">
          {title} login
        </h1>
      </div>

      <div className="bg-[#fcfdfa] border border-[#dcded9] p-6 rounded shadow-sm space-y-6">
        <div className="flex justify-center text-[#8c9185]">
          <Lock size={40} />
        </div>

        <p className="text-sm text-center text-[#555a4f]">
          Skriv dit udleveret password
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input 
              type="password" 
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(false);
              }}
              placeholder="Adgangskode"
              className="w-full border border-[#dcded9] px-3 py-2 rounded text-sm focus:outline-none focus:border-[#2c3223]"
              required
            />
          </div>

          {error && (
            <div className="text-xs text-red-600 flex items-center gap-1.5 bg-red-50 p-2.5 rounded border border-red-200">
              <AlertCircle size={14} className="shrink-0" />
              <span>Forkert adgangskode. Venligst prøv igen.</span>
            </div>
          )}

          <button 
            type="submit"
            className="w-full bg-[#2c3223] text-white font-bold text-xs uppercase tracking-wider py-2.5 rounded hover:bg-[#1e2318] transition-colors"
          >
            Log ind
          </button>
        </form>
      </div>
    </div>
  );
}

// 7. Kontakt Page
function KontaktPage() {
  const [formStatus, setFormStatus] = useState(null);
  const [validationError, setValidationError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const name = formData.get("name");
    const email = formData.get("email");
    const message = formData.get("message");

    if (!name || !email || !message) {
      setValidationError("Følgende felter mangler at blive udfyldt: Navn, Email, Besked");
      return;
    }

    setValidationError("");

    // Submit to the required webform endpoint
    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.fromEntries(formData.entries())),
      });
      if (response.ok) {
        setFormStatus('success');
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="space-y-10">
      <div className="border-b border-[#e9eae7] pb-4">
        <h1 className="text-2xl lg:text-3xl font-bold text-[#2c3223]">
          Kontakt MedicCare
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        
        {/* Contact Info */}
        <div className="space-y-6">
          <div className="bg-[#fcfdfa] border border-[#dcded9] p-6 rounded space-y-3 text-sm text-[#555a4f]">
            <h3 className="font-bold text-[#2c3223] text-base mb-2">MedicCare Hovedkontor</h3>
            <p className="flex items-center gap-2"><User size={16} className="text-[#2c3223]" /> Johnny Andersen</p>
            <p className="pl-6">CMO</p>
            <p className="flex items-center gap-2"><Phone size={16} className="text-[#2c3223]" /> Telefon +45 40 830 830</p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-[#fcfdfa] border border-[#dcded9] p-6 rounded shadow-sm space-y-6">
          <h3 className="font-bold text-[#2c3223] text-base border-b border-[#e9eae7] pb-2">
            Send os en besked
          </h3>

          {formStatus === 'success' ? (
            <div className="bg-green-50 border border-green-200 text-green-800 p-6 rounded text-center space-y-3">
              <div className="flex justify-center text-green-600">
                <CheckCircle2 size={32} />
              </div>
              <h4 className="font-bold">Tak for din besked!</h4>
              <p className="text-xs">Vi vender tilbage til dig hurtigst muligt.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Anti-spam Honeypot field */}
              <input 
                type="text" 
                name="website_url" 
                className="sr-only absolute -top-999 left-0" 
                tabIndex="-1" 
                autoComplete="off" 
              />

              {validationError && (
                <div className="text-xs text-red-600 bg-red-50 p-3 rounded border border-red-200 flex items-start gap-2">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{validationError}</span>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="text-xs text-red-600 bg-red-50 p-3 rounded border border-red-200">
                  Der opstod en fejl under afsendelsen. Prøv venligst igen.
                </div>
              )}

              <div className="space-y-1">
                <label className="block text-xs font-bold text-[#2c3223]">Navn *</label>
                <input 
                  type="text" 
                  name="name"
                  className="w-full border border-[#dcded9] px-3 py-2 rounded text-sm focus:outline-none focus:border-[#2c3223]" 
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-bold text-[#2c3223]">Email *</label>
                <input 
                  type="email" 
                  name="email"
                  className="w-full border border-[#dcded9] px-3 py-2 rounded text-sm focus:outline-none focus:border-[#2c3223]" 
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-bold text-[#2c3223]">Telefon</label>
                <input 
                  type="text" 
                  name="phone"
                  className="w-full border border-[#dcded9] px-3 py-2 rounded text-sm focus:outline-none focus:border-[#2c3223]" 
                />
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-bold text-[#2c3223]">Besked *</label>
                <textarea 
                  name="message"
                  rows="4"
                  className="w-full border border-[#dcded9] px-3 py-2 rounded text-sm focus:outline-none focus:border-[#2c3223] resize-none" 
                  required
                ></textarea>
              </div>

              <p className="text-[10px] text-[#8c9185] italic">* Felter der skal udfyldes</p>

              <button 
                type="submit"
                className="w-full bg-[#2c3223] text-white font-bold text-xs uppercase tracking-wider py-3 rounded hover:bg-[#1e2318] transition-colors shadow-sm"
              >
                Send Besked
              </button>
            </form>
          )}
        </div>

      </div>

      <div className="text-right text-xs text-[#8c9185] italic pt-4">
        MedicCare
      </div>
    </div>
  );
}

// 8. BorgerScreening Page
function BorgerScreeningPage() {
  return (
    <div className="space-y-8">
      <div className="border-b border-[#e9eae7] pb-4">
        <h1 className="text-2xl lg:text-3xl font-bold text-[#2c3223]">
          BorgerScreening
        </h1>
      </div>

      {/* Document Download Box */}
      <div className="bg-[#fcfdfa] border border-[#dcded9] p-6 rounded flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#e9eae7] p-2.5 rounded text-[#2c3223]">
            <FileText size={24} />
          </div>
          <div>
            <h4 className="font-bold text-[#2c3223] text-sm">Talemanus - screening af borgere</h4>
            <p className="text-xs text-[#555a4f]">Hent talemanus til screening af borgere som Word-dokument.</p>
          </div>
        </div>

        <a 
          href="https://mediccare.dk/Dokumenter/TaleManus-screening_af_borgere.docx"
          className="inline-flex items-center gap-2 bg-[#2c3223] text-white font-bold text-xs uppercase tracking-wider px-4 py-2.5 rounded hover:bg-[#1e2318] transition-colors shrink-0"
        >
          <Download size={14} /> Download Word
        </a>
      </div>

      {/* Videos Section */}
      <div className="space-y-8 pt-6 border-t border-[#e9eae7]">
        
        {/* Video 1 */}
        <div className="space-y-3">
          <h3 className="font-bold text-[#2c3223] text-base border-b border-[#e9eae7] pb-1">
            Hvorfor er Medimi®Smart ?
          </h3>
          <div className="aspect-video max-w-2xl mx-auto bg-black rounded overflow-hidden shadow-sm">
            <video 
              src="https://mediccare.dk/video/Intelligent_Medicinh%C3%A5ndtering_mt.mp4"
              controls
              className="w-full h-full object-cover"
              preload="none"
            />
          </div>
        </div>

        {/* Video 2 */}
        <div className="space-y-3 pt-4">
          <h3 className="font-bold text-[#2c3223] text-base border-b border-[#e9eae7] pb-1">
            Hvordan fungere Medimi®Smart så ?
          </h3>
          <div className="aspect-video max-w-2xl mx-auto bg-black rounded overflow-hidden shadow-sm">
            <video 
              src="https://mediccare.dk/video/Medicin_til_tiden%20mt.mp4"
              controls
              className="w-full h-full object-cover"
              preload="none"
            />
          </div>
        </div>

        {/* Video 3 */}
        <div className="space-y-3 pt-4">
          <h3 className="font-bold text-[#2c3223] text-base border-b border-[#e9eae7] pb-1">
            Borger i Aalestrup
          </h3>
          <div className="aspect-video max-w-2xl mx-auto bg-black rounded overflow-hidden shadow-sm">
            <video 
              src="https://mediccare.dk/video/Borgern_Aalestrup_Kort_version.mp4"
              controls
              className="w-full h-full object-cover"
              preload="none"
            />
          </div>
        </div>

      </div>

      <div className="text-right text-xs text-[#8c9185] italic pt-4">
        MedicCare
      </div>
    </div>
  );
}
